"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { io, Socket } from "socket.io-client";

// Define event types to match server
interface ServerToClientEvents {
  game_state: (state: any) => void;
  error: (err: { message: string; code: string }) => void;
  room_created: (data: { roomId: string; joinCode: string }) => void;
  player_joined: (data: { id: string; name: string }) => void;
  player_left: (data: { playerId: string; botTakeover: boolean }) => void;
  cards_drawn: (cards: any[]) => void;
  card_played: (data: { playerId: string; card: any; target?: any }) => void;
  discard_required: (data: { currentCount: number; maxCount: number }) => void;
  game_over: (data: { winnerId: string; winnerName: string; teamId?: number }) => void;
  just_say_no_played: (data: { playerId: string; targetPlayerId: string }) => void;
  sticker_broadcast: (data: { playerId: string; stickerId: string }) => void;
  server_config: (config: any) => void;
  room_terminated: (data: { reason: string }) => void;
  game_invite: (data: { roomId: string; fromPlayerId: string; fromPlayerName: string }) => void;
  waiting_for_match: (data: { message: string }) => void;
  turn_timer_update: (data: { playerId: string; timeRemaining: number; totalTime: number }) => void;
  admin_config_data: (config: any) => void;
}

interface ClientToServerEvents {
  create_room: (data: { mode: string; isPrivate: boolean; playerName: string; settings?: any }) => void;
  matchmake: (data: { mode: string; playerName: string }) => void;
  cancel_matchmake: () => void;
  join_room: (data: { roomId: string; playerName: string }) => void;
  join_by_code: (data: { joinCode: string; playerName: string }) => void;
  add_bot: () => void;
  start_game: () => void;
  play_card: (data: { cardId: string; target?: any }) => void;
  bank_card: (data: { cardId: string }) => void;
  build_card: (data: { cardId: string; colorGroup: string }) => void;
  end_turn: () => void;
  discard_cards: (data: { cardIds: string[] }) => void;
  use_just_say_no: (data: { cardId: string }) => void;
  pay_debt: (data: { cardIds: string[] }) => void;
  leave_room: () => void;
  send_sticker: (data: { stickerId: string }) => void;
  invite_friend: (data: { friendId: string; roomId: string }) => void;
  admin_get_config: () => void;
  admin_update_config: (data: any) => void;
}

type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

interface SocketContextType {
  socket: AppSocket | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  connect: () => {},
  disconnect: () => {},
});

export const useSocket = () => useContext(SocketContext);

// Use an empty string in production to let Socket.io use window.location
// If NEXT_PUBLIC_API_URL is set, use it. Otherwise use relative for production/proxy, or fallback to dev port.
const SERVER_URL = process.env.NEXT_PUBLIC_API_URL || (typeof window !== "undefined" && window.location.hostname === "localhost" ? "http://localhost:3061" : "");

export const SocketProvider = ({ children }: { children: ReactNode }) => {
  const [socket, setSocket] = useState<AppSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // We only connect when explicitly requested or immediately if desired
    const token = localStorage.getItem('monodeal_token');
    const socketInstance: AppSocket = io(SERVER_URL, {
      autoConnect: false,
      withCredentials: true,
      auth: { token }
    });

    socketInstance.on("connect", () => {
      console.log("Socket connected:", socketInstance.id);
      setIsConnected(true);
    });

    socketInstance.on("disconnect", () => {
      console.log("Socket disconnected");
      setIsConnected(false);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  const connect = () => {
    if (socket && !socket.connected) {
      socket.connect();
    }
  };

  const disconnect = () => {
    if (socket && socket.connected) {
      socket.disconnect();
    }
  };

  return (
    <SocketContext.Provider value={{ socket, isConnected, connect, disconnect }}>
      {children}
    </SocketContext.Provider>
  );
};
